import React, { useEffect, useState } from 'react';
import { XMarkIcon, BanknotesIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  useAtualizarMetaCompra,
  useCriarMetaCompra,
  useExcluirMetaCompra,
} from '../../hooks/useMetaCompra';
import { useEmpresas } from '../../hooks/usePesquisas';
import { useToast } from '../ui/Toast';
import { useConfirmDialog } from '../ui/ConfirmDialog';
import InputMoeda from '../ui/InputMoeda';
import { btnPrimario, btnSecundario, btnDanger, inputIris } from '../ui/iris';
import SugestaoMetaInteligente from './SugestaoMetaInteligente';
import type { MetaCompra } from '../../types/metaCompra';

interface Props {
  /** Quando preenchida, abre em modo edição; senão modo criação. */
  meta?: MetaCompra | null;
  /** Pré-seleção pro modo criação (mês default do dashboard). Opcional. */
  defaultAnoMes?: string;
  /** Pré-seleção pro modo criação (empresas filtradas no dashboard). Opcional. */
  defaultEmpresas?: string[];
  onClose: () => void;
}

function anoMesAtual(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const ModalMetaCompra: React.FC<Props> = ({ meta, defaultAnoMes, defaultEmpresas, onClose }) => {
  const ehEdicao = !!meta;
  const toast = useToast();
  const confirm = useConfirmDialog();
  const { data: empresas = [] } = useEmpresas();
  const criarMutation = useCriarMetaCompra();
  const atualizarMutation = useAtualizarMetaCompra();
  const excluirMutation = useExcluirMetaCompra();

  const [empresasSel, setEmpresasSel] = useState<string[]>(meta?.empresas ?? defaultEmpresas ?? []);
  const [anoMes, setAnoMes] = useState<string>(meta?.anoMes ?? defaultAnoMes ?? anoMesAtual());
  const [descricao, setDescricao] = useState<string>(meta?.descricao ?? '');
  const [valorMeta, setValorMeta] = useState<number | null>(meta?.valorMeta ?? null);
  const [observacao, setObservacao] = useState<string>(meta?.observacao ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fecha com Esc
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleEmpresa = (cod: string) => {
    setEmpresasSel((prev) => prev.includes(cod) ? prev.filter((e) => e !== cod) : [...prev, cod]);
  };

  const validar = (): boolean => {
    const errs: Record<string, string> = {};
    if (!descricao.trim()) errs.descricao = 'Descrição obrigatória.';
    if (!ehEdicao) {
      if (empresasSel.length === 0) errs.empresas = 'Selecione ao menos 1 empresa.';
      if (!anoMes || !/^\d{4}-\d{2}$/.test(anoMes)) errs.anoMes = 'Mês obrigatório (formato YYYY-MM).';
    }
    if (valorMeta == null || valorMeta <= 0) {
      errs.valorMeta = 'Valor deve ser maior que zero.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const salvar = async () => {
    if (!validar()) return;
    try {
      if (ehEdicao && meta) {
        await atualizarMutation.mutateAsync({
          id: meta.id,
          req: { descricao: descricao.trim(), valorMeta: valorMeta!, observacao: observacao || null },
        });
        toast.sucesso('Meta atualizada.');
      } else {
        await criarMutation.mutateAsync({
          anoMes,
          descricao: descricao.trim(),
          empresas: empresasSel,
          valorMeta: valorMeta!,
          observacao: observacao || null,
        });
        toast.sucesso('Meta cadastrada.');
      }
      onClose();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string }; status?: number } };
      const msg = e.response?.data?.message
        || (e.response?.status === 409
          ? 'Empresa já está em outra meta neste mês.'
          : 'Falha ao salvar meta.');
      toast.erro(msg);
    }
  };

  const excluir = async () => {
    if (!meta) return;
    const ok = await confirm({
      titulo: 'Excluir meta',
      mensagem: `Remover meta "${meta.descricao}" (${meta.empresas.length} empresa${meta.empresas.length > 1 ? 's' : ''}) em ${meta.anoMes}?`,
      destrutivo: true,
    });
    if (!ok) return;
    try {
      await excluirMutation.mutateAsync(meta.id);
      toast.sucesso('Meta excluída.');
      onClose();
    } catch {
      toast.erro('Falha ao excluir meta.');
    }
  };

  const isLoading = criarMutation.isPending || atualizarMutation.isPending || excluirMutation.isPending;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-meta-titulo"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--scrim)] backdrop-blur-[2px] p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl rounded-iris-lg border border-line-strong bg-surface shadow-iris-xl">
        <div className="flex items-start gap-2 border-b border-line px-4 py-3">
          <BanknotesIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent-text" aria-hidden />
          <h2 id="modal-meta-titulo" className="flex-1 text-sm font-semibold text-tx">
            {ehEdicao ? 'Editar meta mensal' : 'Cadastrar meta mensal'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-tx-3 hover:bg-surface-2"
            aria-label="Fechar"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 p-4">
          {/* Descrição */}
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-tx-3">Descrição</label>
            <input
              type="text"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder='ex: "Grupo Sul", "Atacado SP", "Empresa 01"'
              maxLength={120}
              className={`w-full ${inputIris} ${errors.descricao ? 'border-danger' : ''}`}
            />
            {errors.descricao && <p className="mt-1 text-xs text-danger">{errors.descricao}</p>}
          </div>

          {/* Mês/Ano */}
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-tx-3">Mês / ano</label>
            <input
              type="month"
              value={anoMes}
              onChange={(e) => setAnoMes(e.target.value)}
              disabled={ehEdicao}
              className={`w-full ${inputIris} ${errors.anoMes ? 'border-danger' : ''}`}
            />
            {errors.anoMes && <p className="mt-1 text-xs text-danger">{errors.anoMes}</p>}
          </div>

          {/* Empresas (multiselect chips) */}
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-tx-3">
              Empresas ({empresasSel.length} selecionada{empresasSel.length === 1 ? '' : 's'})
            </label>
            {ehEdicao ? (
              <div className="flex flex-wrap gap-1 rounded-iris-sm border border-line bg-surface-2 p-2 opacity-70">
                {meta!.empresas.map((cod) => (
                  <span
                    key={cod}
                    className="rounded-iris-sm border border-accent bg-accent-soft px-2 py-1 text-[11px] text-accent-text"
                  >
                    <span className="font-mono">{cod}</span>
                    <span className="ml-1">{empresas.find((e) => e.codigo === cod)?.nomeFantasia || ''}</span>
                  </span>
                ))}
              </div>
            ) : (
              <div className={`flex flex-wrap gap-1 rounded-iris-sm border bg-surface p-2 ${
                errors.empresas ? 'border-danger' : 'border-line'
              }`}>
                {empresas.map((e) => {
                  const ativa = empresasSel.includes(e.codigo);
                  return (
                    <button
                      key={e.codigo}
                      type="button"
                      onClick={() => toggleEmpresa(e.codigo)}
                      className={`rounded-iris-sm border px-2 py-1 text-[11px] transition-colors ${
                        ativa
                          ? 'border-accent bg-accent-soft text-accent-text'
                          : 'border-line bg-surface text-tx-2 hover:bg-surface-2'
                      }`}
                      title={e.razaosocial}
                    >
                      <span className="font-mono">{e.codigo}</span>
                      <span className="ml-1">{e.nomeFantasia || e.razaosocial}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {errors.empresas && <p className="mt-1 text-xs text-danger">{errors.empresas}</p>}
            {ehEdicao && (
              <p className="mt-1 flex items-center gap-1 text-[10px] text-tx-3">
                <ExclamationTriangleIcon className="h-3 w-3" />
                Empresas e mês não podem ser alterados. Exclua e cadastre nova meta pra ajustar.
              </p>
            )}
          </div>

          {/* Sugestão inteligente baseada no último trimestre fechado.
              Renderiza só com empresas selecionadas; sem dimensão = consolidada. */}
          <SugestaoMetaInteligente
            empresas={ehEdicao ? meta!.empresas : empresasSel}
            onAplicar={setValorMeta}
          />

          {/* Valor da meta */}
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-tx-3">Valor da meta</label>
            <InputMoeda
              value={valorMeta}
              onChange={setValorMeta}
              hasError={!!errors.valorMeta}
              ariaLabel="Valor da meta em reais"
            />
            {errors.valorMeta && <p className="mt-1 text-xs text-danger">{errors.valorMeta}</p>}
          </div>

          {/* Observação */}
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-tx-3">Observação (opcional)</label>
            <textarea
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Justificativa, contexto, premissas..."
              className="w-full resize-none rounded-iris-sm border border-line-strong bg-surface-2 px-3 py-1.5 text-sm text-tx placeholder:text-tx-3 focus:outline-none focus:bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent-soft"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-line px-4 py-3">
          {ehEdicao ? (
            <button
              type="button"
              onClick={excluir}
              disabled={isLoading}
              className={btnDanger}
            >
              Excluir
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className={btnSecundario}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={salvar}
              disabled={isLoading}
              className={btnPrimario}
            >
              {isLoading ? 'Salvando…' : ehEdicao ? 'Salvar' : 'Cadastrar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalMetaCompra;
